import {useCallback} from 'react';

import {EMBED_EDITABLE_REPORT_SETTINGS, REPORT_MODULES_IDS, SNACKBAR_ACTIONS} from '../../../../const';
import {useIsModuleVisibleOrEnabled} from '../../../../hooks/components/report-modules/useIsModuleVisibleOrEnabled/useIsModuleVisibleOrEnabled';
import useBreakpoint from '../../../../hooks/dom/useBreakpoint';
import useProjectCrudModal from '../../../../hooks/dom/useProjectCrudModal';
import useBudget from '../../../../hooks/providers/useBudget';
import useEmmai from '../../../../hooks/providers/useEmmai';
import useProfile from '../../../../hooks/providers/useProfile';
import useProjectsCustomMappings from '../../../../hooks/providers/useProjectsCustomMappings';
import useReport from '../../../../hooks/providers/useReport';
import useReportSummary from '../../../../hooks/providers/useReportSummary';
import useReportViews from '../../../../hooks/providers/useReportViews';
import useSnackbar from '../../../../hooks/providers/useSnackbar';
import useUpload from '../../../../hooks/providers/useUpload';
import useWorkspaces from '../../../../hooks/providers/useWorkspaces';
import {setProjectFilterSlicer} from '../../../../utils';

const useReportModuleCoreHandler = () => {
  const {
    reports,
    selectedReport,
    selectedProject: project,
    setAccountantDocumentRequestModalOpen,
    setIsReportInEditionMode,
    setIsReportExportTypeChoiceModalOpen,
    reportSelectedFoldersSirens,
    setCanRefreshReport,
    setIsReportLoaded
  } = useReport();
  const {
    setIsSpreadsheetModalOpen,
    setIsImportBudgetModalOpen,
    setIsExportBudgetModalOpen,
    setIsCreateOrUpdateBudgetFormOpen,
    setIsCreateBudgetForm,
    setIsDeleteBudgetDialogOpen,
    alreadyTakenBudgetNames: budgets,
    setSelectedBudget
  } = useBudget();
  const {setIsSaveReportViewModalOpen, setIsChooseViewPopperOpen, setChooseViewPopperAnchorEl, setIsReportPristine} = useReportViews();
  const {showSnackbar} = useSnackbar();
  const {profile} = useProfile();
  const {setReportSummaryModalOpen} = useReportSummary();
  const {setIsUploadFilesModalOpen, setProjectSelected} = useUpload();
  const {setCustomizeMappingModalOpen, setResetCustomMappingsConfirmationModalOpen} = useProjectsCustomMappings();
  const {
    setRightClickedReportTabId: setReportToDelete,
    setDeleteWorkspaceOrReportModalOpen: setDeleteReportModalOpen,
    workspaces,
    setCreateReportModalOpen,
    isUserOwnerOfWorkspace,
    setIsSelectWorkspaceBeforeReportCreationModalOpen,
    setSelectedWorkspace
  } = useWorkspaces();
  const {setMonthlyRestitutionModalOpen} = useEmmai();
  const breakpoint = useBreakpoint();

  const {openModal: openCustomMappingsModal} = useProjectCrudModal({
    setModalOpen: setCustomizeMappingModalOpen,
    project
  });

  return useCallback(
    // eslint-disable-next-line complexity
    async (module, event) => {
      const currentReport = reports.find(r => r.report_id === selectedReport?.config?.id);

      console.log({module, budgets, event});
      switch (module.id) {
        case REPORT_MODULES_IDS.DENTAL_MONTHLY_RESTITUTION:
          setMonthlyRestitutionModalOpen(true);
          break;
        case REPORT_MODULES_IDS.CREATE_BUDGET:
          console.log('Here click create budget');
          setIsCreateOrUpdateBudgetFormOpen(true);
          setIsCreateBudgetForm(true);
          break;
        case REPORT_MODULES_IDS.EDIT_BUDGET:
          if (budgets.length === 1) {
            setSelectedBudget(budgets[0]);
          }
          setIsSpreadsheetModalOpen(true);
          break;
        case REPORT_MODULES_IDS.DELETE_BUDGET:
          setIsDeleteBudgetDialogOpen(true);
          break;
        case REPORT_MODULES_IDS.IMPORT_BUDGET:
          setIsImportBudgetModalOpen(true);
          break;
        case REPORT_MODULES_IDS.EXPORT_BUDGET:
          if (budgets.length === 1) {
            setSelectedBudget(budgets[0]);
          }
          setIsExportBudgetModalOpen(true);
          break;
        case REPORT_MODULES_IDS.CUSTOMIZE_MAPPINGS:
          openCustomMappingsModal();
          break;
        case REPORT_MODULES_IDS.RESET_CUSTOMIZE_MAPPINGS:
          setResetCustomMappingsConfirmationModalOpen(true);
          break;
        case REPORT_MODULES_IDS.UPDATE_REPORT_SUMMARY:
          setReportSummaryModalOpen(true);
          break;
        case REPORT_MODULES_IDS.SHARE_DOCUMENT_WITH_ACCOUNTANT:
          setAccountantDocumentRequestModalOpen(true);
          break;

        case REPORT_MODULES_IDS.CREATE_REPORT: {
          const userOwnedWorkspaces = workspaces.filter(w => isUserOwnerOfWorkspace(profile.id, w));
          if (userOwnedWorkspaces.length === 1) {
            setSelectedWorkspace(userOwnedWorkspaces[0].workspace_id);
            setCreateReportModalOpen(true);
          } else {
            setIsSelectWorkspaceBeforeReportCreationModalOpen(true);
          }
          break;
        }
        case REPORT_MODULES_IDS.DELETE_REPORT: {
          setDeleteReportModalOpen(true);
          setReportToDelete(currentReport.report_id);
          break;
        }
        case REPORT_MODULES_IDS.EDIT_REPORT:
          await selectedReport.switchMode('edit');
          await selectedReport.updateSettings(EMBED_EDITABLE_REPORT_SETTINGS);
          setIsReportInEditionMode(true);
          break;
        case REPORT_MODULES_IDS.EXPORT_REPORT:
          setIsReportExportTypeChoiceModalOpen(true);

          break;
        case REPORT_MODULES_IDS.REFRESH_REPORT:
          await selectedReport.refresh();
          setCanRefreshReport(false);
          showSnackbar(SNACKBAR_ACTIONS.UPDATE_SPREADSHEET_DATA_SUCCESS);
          setTimeout(() => {
            setCanRefreshReport(true);
          }, 15000);
          break;
        case REPORT_MODULES_IDS.UPLOAD_DATA:
          setIsUploadFilesModalOpen(true);
          setProjectSelected(project);
          break;
        case REPORT_MODULES_IDS.SAVE_VIEW:
          setIsSaveReportViewModalOpen(true);
          break;
        case REPORT_MODULES_IDS.SELECT_VIEW:
          setIsChooseViewPopperOpen(true);
          setChooseViewPopperAnchorEl(event.currentTarget);
          break;
        case REPORT_MODULES_IDS.RESET_VIEW:
          setIsReportLoaded(false);

          await selectedReport.reload();

          if (project && currentReport.homepage) {
            await new Promise(resolve => {
              setTimeout(async () => {
                await setProjectFilterSlicer(selectedReport, project.project_name);
                await selectedReport.setPage(currentReport.homepage);
                resolve();
              }, 1000);
            });
          }

          showSnackbar(SNACKBAR_ACTIONS.RESET_REPORT_VIEW_SUCCESS);
          setIsReportLoaded(true);
          setIsReportPristine(true);
          break;

        default:
          // eslint-disable-next-line no-console
          console.log('Unknown module id :', module.id);
          break;
      }
    },
    [project, breakpoint, reports, workspaces, isUserOwnerOfWorkspace, selectedReport, project, budgets, reportSelectedFoldersSirens]
  );
};

export default useReportModuleCoreHandler;
